import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCoins } from "@fortawesome/free-solid-svg-icons";
import "./RemoteAnalysisButton.css";
import LiveAnalysisCharts, { liveMetrics } from "./LiveAnalysisCharts";
import AnalysisCountdown, { AnalysisCanceled } from "./AnalysisCountdown";
import DisclosureIcon from "./DisclosureIcon";
const activeStates = ["Pending", "Approved", "Running"];
const labels = {
    Pending: "Waiting for permission on the device", Approved: "Approved — waiting for the device",
    Running: "Five-minute analysis in progress", Completed: "PDF generated",
    Denied: "The device user declined", Cancelled: "Analysis cancelled", Expired: "Request expired",
    Failed: "Analysis could not finish", NoCredits: "No report credits available in your account",
    Queued: "Waiting to send", Sending: "Submitting to email provider", Accepted: "Accepted by email provider",
    Rejected: "Email provider rejected this copy", Uncertain: "Email acceptance could not be confirmed",
};
export default function RemoteAnalysisButton({ apiBaseUrl, token, targetUserId, targetName, meetingSessionId, webMeetingId, renderInline, backgroundRequest }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [connected, setConnected] = useState(false);
  const [approvalBasis, setApprovalBasis] = useState("DeviceConsent");
  const [loaded, setLoaded] = useState(false);
    const [request, setRequest] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [credits, setCredits] = useState(null);
    const [loadingCredits, setLoadingCredits] = useState(false);
    const [inlineAnalysis, setInlineAnalysis] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const expansionToggle = useRef(null);
    const focusOnHandoff = useRef(true);
    const skipLatest = useRef(false);
    const dialog = useRef(null);
    const requestId = useRef("");
    const mutationVersion = useRef(0);
    const mutationBusy = useRef(false);
    const currentRequest = useRef(null);
    currentRequest.current = request;
    const seenRequestIds = useRef(new Set());
    if (request?.requestId) seenRequestIds.current.add(request.requestId);
    const statusView = submitted || Boolean(request);
    const context = `targetUserId=${targetUserId}${meetingSessionId ? `&meetingSessionId=${meetingSessionId}` : ""}${webMeetingId ? `&webMeetingId=${encodeURIComponent(webMeetingId)}` : ""}`;
    const api = useCallback(async (path, method = "GET", body, signal) => {
        if (!token)
            throw new Error("Sign in to request an analysis.");
        const response = await fetch(`${apiBaseUrl.replace(/\/$/, "")}/api/remote-analysis${path}`, {
            method, signal, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const failure = new Error(response.status === 401 ? "Sign in again to request an analysis."
                : data.message || "Remote analysis is unavailable. Try again shortly.");
            failure.status = response.status; throw failure;
        }
        return data;
    }, [apiBaseUrl, token]);
    useEffect(() => {
        if (!open)
            return;
        let cancelled = false;
        setBusy(true);
        setError("");
        setRequest(null);
        setSubmitted(false);
        setConnected(false); setLoaded(false);
        requestId.current = crypto.randomUUID().replace(/-/g, "");
        const startingNew = skipLatest.current;
        skipLatest.current = false;
        Promise.all([api(`/current-device?${context}`), startingNew ? Promise.resolve({ request: null }) : api(`/latest?${context}`)])
            .then(([available, latest]) => {
            if (cancelled)
                return;
            setLoaded(true); setConnected(available.connected); setApprovalBasis(available.approvalBasis || "DeviceConsent");
            setRequest(latest.request);
        })
            .catch(e => { if (!cancelled)
            setError(e.message); })
            .finally(() => { if (!cancelled)
            setBusy(false); });
        return () => { cancelled = true; };
    }, [open, api, context]);
    useEffect(() => {
        if (open && !dialog.current?.open) dialog.current?.showModal();
    }, [open, statusView]);
    useEffect(() => {
        if (!open || !token) return;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        let active = true;
        setLoadingCredits(true);
        setCredits(null);
        fetch(`${apiBaseUrl.replace(/\/$/, "")}/api/credits/account`, {
            method: "GET", signal: controller.signal, headers: { Authorization: `Bearer ${token}` },
        }).then(async response => {
            const account = await response.json();
            if (!response.ok || !Number.isInteger(account.availableReports) || account.availableReports < 0) throw new Error();
            if (active) setCredits(account.availableReports);
        }).catch(() => { if (active) setCredits(null); })
            .finally(() => { if (active) setLoadingCredits(false); });
        return () => { active = false; clearTimeout(timeout); controller.abort(); };
    }, [open, apiBaseUrl, token]);
    useEffect(() => {
        if (!backgroundRequest?.requestId) return;
        if (currentRequest.current?.requestId === backgroundRequest.requestId || seenRequestIds.current.has(backgroundRequest.requestId) || mutationBusy.current) return;
        seenRequestIds.current.add(backgroundRequest.requestId);
        mutationVersion.current++;
        focusOnHandoff.current = false;
        setRequest(backgroundRequest);
        setInlineAnalysis({ requestId: backgroundRequest.requestId, telemetry: backgroundRequest.liveTelemetry });
        setExpanded(!webMeetingId || activeStates.includes(backgroundRequest.state));
        setOpen(false);
        setError("");
    }, [backgroundRequest, webMeetingId]);
    // A team row takes over only after actual samples arrive (zero is valid).
    const inlineEnabled = Boolean(renderInline);
    const inlineRequestId = inlineAnalysis?.requestId;
    useEffect(() => {
        const telemetry = request?.liveTelemetry;
        const hasSamples = liveMetrics.some(({ key }) =>
            Array.isArray(telemetry?.snapshot?.[key]) && telemetry.snapshot[key].some(value => Number.isFinite(value) && value >= 0));
        if (!inlineEnabled || !hasSamples) return;
        setInlineAnalysis({ requestId: request.requestId, telemetry });
        if (inlineRequestId !== request.requestId) {
            setExpanded(true);
            setOpen(false);
        }
    }, [inlineEnabled, inlineRequestId, request]);
    useEffect(() => {
        if (inlineRequestId && focusOnHandoff.current) expansionToggle.current?.focus();
    }, [inlineRequestId]);
    const isActive = activeStates.includes(request?.state);
    const tracking = open || (inlineEnabled && isActive) || Boolean(inlineRequestId && inlineRequestId === request?.requestId);
    const needsPolling = request && (activeStates.includes(request.state) || request.deliveries?.some(d => ["Queued", "Sending"].includes(d.state)));
    const polledRequestId = request?.requestId;
    const readOnly = request?.readOnly === true;
    const pollingInterval = request && activeStates.includes(request.state) ? 2000 : 10000;
    useEffect(() => {
        if (!tracking || !needsPolling || !polledRequestId) return;
        let cancelled = false;
        let fetching = false;
        let timer;
        let controller;
        let failures = 0;
        async function poll() {
            if (cancelled || fetching || document.hidden) return;
            if (mutationBusy.current) { timer = setTimeout(poll, pollingInterval); return; }
            fetching = true;
            const version = mutationVersion.current;
            controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 8000);
            try {
                const path = readOnly ? `/${polledRequestId}/shared${webMeetingId ? `?webMeetingId=${encodeURIComponent(webMeetingId)}` : ""}` : `/${polledRequestId}?includeLive=true`;
                const updated = await api(path, "GET", undefined, controller.signal);
                if (!cancelled && version === mutationVersion.current) {
                    setRequest(updated);
                    setError("");
                    failures = 0;
                }
            } catch (reason) {
                if (!cancelled && !document.hidden && version === mutationVersion.current) {
                    if (readOnly && [401, 403, 404].includes(reason.status)) {
                        seenRequestIds.current.delete(polledRequestId);
                        setRequest(null); setInlineAnalysis(null); setError("This shared analysis is no longer available."); return;
                    }
                    failures++;
                    setError("Status could not be refreshed. Your existing request is unchanged.");
                }
            } finally {
                clearTimeout(timeout);
                fetching = false;
                if (!cancelled && !document.hidden) timer = setTimeout(poll, Math.min(30000, pollingInterval * Math.pow(2, failures)));
            }
        }
        const visibility = () => {
            clearTimeout(timer);
            if (document.hidden) controller?.abort();
            else poll();
        };
        document.addEventListener("visibilitychange", visibility);
        poll();
        return () => { cancelled = true; clearTimeout(timer); controller?.abort(); document.removeEventListener("visibilitychange", visibility); };
    }, [tracking, api, needsPolling, polledRequestId, pollingInterval, readOnly, webMeetingId]);
    async function submit(path, body) {
        if (busy || mutationBusy.current)
            return;
        if (path === "") setSubmitted(true);
        setBusy(true);
        mutationBusy.current = true;
        mutationVersion.current++;
        setError("");
        try {
            setRequest(await api(path, "POST", body));
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "The request could not be confirmed. Retry the same request.");
        }
        finally {
            mutationBusy.current = false;
            setBusy(false);
        }
    }
    function startNewAnalysis() {
        if (busy || mutationBusy.current || isActive) return;
        requestId.current = crypto.randomUUID().replace(/-/g, "");
        setInlineAnalysis(null);
        focusOnHandoff.current = true;
        setRequest(null);
        setSubmitted(false);
        setError("");
        if (!open) {
            skipLatest.current = true;
            setOpen(true);
        }
    }
    function requestAnalysis() {
        void submit("", {
            requestId: requestId.current, targetUserId, meetingSessionId: meetingSessionId || null,
            ...(webMeetingId ? { webMeetingId } : {}),
        });
    }
    const inlineProgress = ["Running", "Cancelled"].includes(request?.state) && request.requestId === inlineRequestId;
    const creditMessage = {
        Reserved: "One credit is reserved until the report completes. It is returned if analysis fails or is cancelled.",
        Consumed: "One report credit used. Email retries use no extra report credit.",
        Released: "The reserved credit was returned.",
    }[request?.creditState];
    const requestStatus = request && <section aria-live="polite">
        {!inlineProgress && <h3>{request.state === "Cancelled" ? <AnalysisCanceled /> : <>{labels[request.state] || request.state}
          {request.state === "Pending" && <span className="remoteAnalysisWaitingDots" aria-hidden="true"><span>.</span><span>.</span><span>.</span></span>}
          {request.state === "Running" && <AnalysisCountdown key={request.requestId} telemetry={request.liveTelemetry} />}</>}</h3>}
        {!readOnly && <>{creditMessage && <p>{creditMessage}</p>}
        {request.deliveries?.map(d => <p key={d.recipient}><strong>{d.recipient}:</strong> {labels[d.state] || d.state}</p>)}
        {request.deliveries?.some(d => d.state === "Uncertain") && <p>A copy may already have been sent. It will not be sent again automatically.</p>}
        <footer>
          {isActive && <button type="button" disabled={busy} onClick={() => void submit(`/${request.requestId}/cancel`)}>Cancel analysis</button>}
          {request.deliveries?.some(d => d.state === "Rejected") && <button type="button" disabled={busy} onClick={() => void submit(`/${request.requestId}/retry-delivery`)}>Retry rejected email</button>}
          {!isActive && <button type="button" disabled={busy} onClick={startNewAnalysis}>New analysis</button>}
        </footer>
        </>}
      </section>;
    const action = <span className="remoteAnalysisAction" onClick={e => e.stopPropagation()}>
    <button type="button" className="secondaryButton compactAction remoteAnalyzeButton" disabled={targetUserId <= 0 || busy || isActive} onClick={() => {
        if (request?.state === "Cancelled" || (readOnly && !isActive)) startNewAnalysis();
        else if (inlineAnalysis) { setExpanded(true); expansionToggle.current?.focus(); }
        else setOpen(true);
    }} title={targetUserId <= 0 ? "This participant must sign in to SignalTuner first" : "Request a PDF analysis"}>Analyze</button>
    {open && <dialog key={statusView ? "status" : "request"} ref={dialog} className={`remoteAnalysisDialog${!inlineEnabled && (request?.state === "Running" || request?.liveTelemetry) ? " remoteAnalysisDialog--live" : ""}`} onCancel={() => setOpen(false)} onClose={() => setOpen(false)} aria-label={statusView ? `Analysis status for ${targetName}` : `Analyze ${targetName}`}>
      <header><h2>{statusView ? `Analysis status for ${targetName}` : `Analyze ${targetName}`}</h2><button type="button" aria-label="Close analysis request" onClick={() => setOpen(false)}>×</button></header>
      {!statusView && <>
      <p>A five-minute analysis creates a PDF emailed to you and this user. <strong>Your account pays one report credit.</strong></p>
      <p>{approvalBasis === "Self" ? "Your analysis starts automatically on your desktop. You can stop it at any time."
        : approvalBasis === "TeamAuthority" ? "As this team's Owner or Admin, you can start analysis without member approval. The device user can stop the analysis."
        : approvalBasis === "MemberApproval" ? "Your teammate must approve each analysis on their device. Saved permissions do not bypass team member approval."
        : "The device user can choose Deny, Just once, or Always. Saved permission applies only to you on that device and can be revoked in their desktop Settings."}</p>
      <p>Credits are used in this order: your team allocation, the general fund for Owners and Admins, your monthly allowance, then personal credits.</p>
      {!meetingSessionId && <p>{webMeetingId ? "Live charts and status are visible to joined participants in this meeting." : "Live report charts and status are visible to current members of your team."}</p>}
      </>}
      {error && <p role="alert" className="remoteAnalysisError">{error}</p>}
      {busy && <p role="status">{statusView && !request ? "Requesting analysis…" : "Updating…"}</p>}
      {!inlineEnabled && (request?.state === "Running" || request?.liveTelemetry) && <LiveAnalysisCharts telemetry={request.liveTelemetry} running={request.state === "Running"} />}
      {request ? requestStatus : statusView ? !busy && <footer><button type="button" onClick={requestAnalysis}>Retry analysis request</button></footer> : !busy && loaded && <section>
        {connected ? <>
          <p>Analysis will run on the currently signed-in desktop.</p>
          <footer className="remoteAnalysisRequestActions"><button className="remoteAnalysisRequestButton" type="button" disabled={busy || !connected} onClick={requestAnalysis}>Request analysis · 1 credit<FontAwesomeIcon icon={faCoins} className="remoteAnalysisCreditIcon" aria-hidden="true" /></button>
            <p aria-live="polite">Available credits: {loadingCredits ? "Loading…" : credits ?? "Unavailable"}{!loadingCredits && credits !== null && <FontAwesomeIcon icon={faCoins} className="remoteAnalysisCreditIcon" aria-hidden="true" />}</p>
          </footer>
        </> : <p>The updated SignalTuner desktop app must be running and signed in on this user’s device. Reopen this dialog after they connect.</p>}
      </section>}
    </dialog>}
  </span>;
    const panel = inlineAnalysis && request?.requestId === inlineRequestId && <section className="remoteAnalysisInline" aria-label={`Analysis for ${targetName}`}>
      <button ref={expansionToggle} type="button" className="remoteAnalysisToggle" aria-expanded={expanded}
        aria-controls={`member-analysis-${targetUserId}`} onClick={() => setExpanded(value => !value)}>
        <span>Analysis for {targetName}</span><DisclosureIcon />
      </button>
      <div id={`member-analysis-${targetUserId}`} hidden={!expanded} className="remoteAnalysisInlineBody">
        {inlineProgress && <p className="remoteAnalysisProgress">{request.state === "Cancelled" ? <AnalysisCanceled /> : <>{labels.Running}<AnalysisCountdown key={request.requestId} telemetry={request.liveTelemetry} /></>}</p>}
        {error && <p role="alert" className="remoteAnalysisError">{error}</p>}
        {busy && <p role="status">Updating...</p>}
        <LiveAnalysisCharts telemetry={inlineAnalysis.telemetry} running={request.state === "Running"} />
        {requestStatus}
      </div>
    </section>;
    // Let the row place the action and full-width panel without moving request ownership.
    return renderInline ? renderInline({ action, panel }) : action;
}
